import { BaseFormData, QueryData } from '@aibi-ui/core';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { InteractQuestionItem } from 'src/conversation/components/ProcessStep/types';
import { RootState } from 'src/views/store';

export type ActionReturn<R = void> = ThunkAction<R, RootState, null, AnyAction>;

export type JsonString = string;

export type AnswerStatus = 'completed' | 'waiting' | 'answering' | 'stoping';

/** 对话类型 */
export enum EnumConversationType {
  /** 主题分析 */
  TOPIC = 'topic',
  /** 综合分析 */
  COMPREHENSIVE = 'comprehensive',
}

/** 基础对话历史记录 */
export interface BaseHistoryDataItem {
  analysis: null | string;
  changed_by_fk: null | string;
  changed_on: null | string;
  chart_data: null | string;
  code_query: null | string;
  code_subquery: null | string;
  conversation_id: string;
  created_by_fk: null;
  created_on: null;
  feedback: null;
  feedback_type: null;
  first_round: boolean;
  has_data: boolean;
  id: string;
  intermediate_steps: JsonString;
  is_error: boolean;
  is_finished: boolean;
  is_interrupted: boolean;
  likes: null;
  plan: null;
  pred: null | JsonString;
  response: string;
  round_id: string;
  status: 1;
  task: null;
  time: string;
  title: string;
  /** 主题id字符串 */
  topic: string;
  user_id: string;
  user_input: string;
}

/** 后端返回的对话历史记录项 */
export interface ConversationHistoryDataItem extends BaseHistoryDataItem {
  rn: number;
}

/** 对话历史记录详情 */
export interface ChatHistoryDataItem extends BaseHistoryDataItem {
  /** 往期回复 */
  reply: Omit<BaseHistoryDataItem, 'status'>[];
}

/** 分享对话记录详情 */
export interface ShareConversationHistoryDataItem
  extends Omit<BaseHistoryDataItem, 'intermediate_steps'> {
  intermediate_steps: string[];
}

/** 前端需要的数据结构 */
export interface ConversationHistoryItem extends ConversationHistoryDataItem {
  checked: boolean;
  name: string;
  type: number;
}

/** 一条对话 */
export type ChatListItem = HumanChatItem | AIChatItem;

/** 每条对话公共的字段 */
export interface BaseChatListItem {
  checked?: boolean;
  _key: string;
}

/** 用户输入的 */
export interface HumanChatItem
  extends Partial<BaseHistoryDataItem>,
    BaseChatListItem {
  type: 'human';
  input: string;
  /** 暂时只有分享对话的时候才有这个字段 */
  username?: string;
}

/** ai回复内容数据 */
export interface AIChatItem extends BaseChatListItem {
  id?: string;
  type: 'ai';
  output: AIChatItemOutput;
  renderingState: EnumConversationRenderingState;
}

/** ai回复内容output */
export interface AIChatItemOutput
  extends Pick<
    BaseHistoryDataItem,
    | 'status'
    | 'response'
    | 'analysis'
    | 'user_input'
    | 'conversation_id'
    | 'round_id'
    | 'topic'
  > {
  pred?: null | string[];
  chart?: null | ChartResponse;
  stream_end: boolean;
  /** 查询步骤 */
  steps: { content: string; end: boolean }[];
  /** 查询步骤是否展开 */
  isExpanded: boolean;
  /** 回答是否中断 */
  interrupted?: boolean;
  /** 是否展示数据分析步骤跳转提示 */
  showJustifyTip?: boolean;
}

/** 开始对话所需的参数 */
export interface ConversationParams {
  conversation_id: string;
  round_id: string;
  content: string;
  topic: string;
  topic_id: number;
  enable_hil: boolean;
}

export interface ShareConversationInfo {
  title: null | string;
  ids: number[];
  user_id: null | string;
  user_name: null | string;
}

/** 对话sse数据中的type类型 */
export type ChatEventDataType =
  | 'Process'
  | 'Chunk'
  | 'Final'
  | 'History'
  | 'Chart'
  | 'Entity'
  | 'Pred';

/** 一条event 数据 */
export type ChatEventDataItem =
  | ChatProcessEventDataItem
  | ChatChunkEventDataItem
  | ChatFinalEventDataItem
  | ChatHistoryEventDataItem
  | ChatChartEventDataItem
  | ChatEntityEventDataItem
  | ChatPredEventDataItem;

export interface BaseChatEventDataItem {
  type: ChatEventDataType;
  username: string;
  response: string;
  stream_end: boolean;
  has_streamed: boolean;
  // interrupted: boolean;
  time_elaspsed: number;
}

/** 中间步骤 */
export interface ChatProcessEventDataItem extends BaseChatEventDataItem {
  type: 'Process';
}

/** 回复文字流 */
export interface ChatChunkEventDataItem extends BaseChatEventDataItem {
  type: 'Chunk';
  response_type: 'analysis' | 'response';
}

/** 图表数据 */
export interface ChatChartEventDataItem
  extends Omit<BaseChatEventDataItem, 'response'> {
  type: 'Chart';
  response: ChartResponse;
}

/** type为Chart时的response内容 */
export interface ChartResponse {
  title: string;
  chart_id: number;
  form_data: BaseFormData;
  queriesResponse: QueryData[];
}

/** 最终的一条回复 */
export interface ChatFinalEventDataItem extends BaseChatEventDataItem {
  type: 'Final';
  analysis: string;
  chart: ChartResponse;
  pred: string[];
  has_data: boolean;
  is_error: boolean;
  is_interrupted: boolean;
}

/** 历史记录type */
export interface ChatHistoryEventDataItem
  extends Omit<BaseChatEventDataItem, 'response'> {
  type: 'History';
  response: (
    | ChatProcessEventDataItem
    | ChatChunkEventDataItem
    | ChatFinalEventDataItem
    | ChatChartEventDataItem
    | ChatEntityEventDataItem
    | ChatPredEventDataItem
  )[];
}

/** 推荐追问 */
export interface ChatPredEventDataItem
  extends Omit<BaseChatEventDataItem, 'response'> {
  type: 'Pred';
  response: string[];
}

/** 实体检测回复内容 */
export interface ChatEntityEventDataItem
  extends Omit<BaseChatEventDataItem, 'response'> {
  type: 'Entity';
  response: InteractQuestionItem[];
  wait_end: string;
}

/** 当前正在进行中的对话打字的进程 */
export enum EnumConversationRenderingState {
  /** 查询步骤 */
  step = 1,
  /** 数据解读 */
  analysis = 2,
  /** 回复结果 */
  response = 3,
  /** 回复完成后的actions */
  actions = 4,
}

/** 佐证系统相关类型 */
export declare namespace ChatJustify {
  /** 佐证数据 */
  interface DataItem {
    /** 步骤序号，从1开始 */
    step: number;
    /** 该步骤的执⾏sql */
    sql: string;
    /** ai⽣成的该步骤的⽂字描述 */
    caption: string;
    /** 该步骤涉及的数据集以及其对应id */
    tables: { id: number; name: string }[];
    /** 该步骤涉及的连表条件，每个条件格式为 <表名>.<列名> <运算符> <表名>.<列名> */
    joins: string[];
    /** 该步骤可供⽤户选择下钻的字段 */
    drillables: string[];
    /** 该步骤sql执⾏获得的数据，每个json代表⼀列数据，每个元素为列名和其对应数值的键值对 */
    data: Record<string, any>[];
    /** 步骤的目标 */
    goal: string;
    /** 列表数据标题 */
    title: string;
  }

  /** 查询佐证数据所需参数 */
  interface ListQuery {
    conversation_id: string;
    round_id: string;
    topic_id: number;
  }

  /** 导出佐证数据 */
  interface ExportQuery extends ListQuery {
    step: number;
  }

  /** 下钻数据 */
  interface DrillData {
    /** 下钻所执⾏的sql */
    drill_sql: string;
    /** 下钻获得的数据。每个json为列名和其对应数值的键值对 */
    drill_data: Record<string, any>[];
    /** ⽤户选择的下钻字段在本表中涉及的字段，可⾼亮这些列 */
    highlights: string[];
  }

  interface DrillQuery {
    /** 步骤序号，表⽰⽤户选择的是哪⼀个步骤中的下钻字段 */
    step: number;
    /** ⽤户选择的数据所属字段，即下钻字段 */
    colname: string;
    /** ⽤户选择的数据所在的⾏的⾏数据，为列名和其对应数值的键值对 */
    row_data: DataItem['data'][number];
    conversation_id: string;
    round_id: string;
    topic_id: number;
  }
}
